import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import { mapActions, mapState } from 'vuex';
import VideoService from '@/services/video.service';
import {
  CameraStatus,
  EventBusEvents,
  FragmentHoleType,
  KVSErrorCodes,
  PlayerStatus,
  VideoControllersType,
} from '../globals/enums';
import VideoArea from '@/components/VideoArea.vue';
import Slider from './inputs/ZoomSlider.vue';
import VideoControllers from '@/components/VideoControllers.vue';
import CameraMenu from '@/components/CameraMenu.vue';
import panzoom from 'panzoom';
import {
  getScaleMultiplier,
  getTransformOriginOffset,
  getOffsetXY,
} from '../utils/coordinates';
import KVService from '@/services/kinesis/kv.service';
import EventBus from '@/utils/eventBus';
import { KVWebRTCService } from '@/services/kinesis/kv-webrtc.service';
import {
  checkHLSEndTimestamp,
  convertToISO8601,
  getFiniteNumber,
  getHLSLiveStreamStartTimestamp,
  getRandomId,
} from '@/utils/helpers';
import { Unwanted_Pause_Timeout_Duration_Ms } from '@/globals/constants';
import { PerfectTimer } from '@/utils/perfectTimer';
import { tracer, VideoStreamPrint } from '@/utils/print';
import { VideoStreamErrorMessages } from '@/globals/errorMessages';
import * as handlers from '../utils/playerStatus.util';
import {
  checkVideoOffEnded,
  findCurrentSegment,
  getFragmentByTimestamp,
  handleVideoHole,
  isTimeCloseToLive,
} from '@/utils/video.util';
import { seekBackwardTimestamp } from '@/utils/seekBackward.util';
import { seekTimestamp } from '@/utils/seekTimestamp';
import { seekForwardTimestamp } from '@/utils/seekForward.util';
import {
  endDiscontinuity,
  handleVideoOff,
  resetDiscontinuity,
} from '@/utils/discontinuity.util';
// Timeout keys
const recreateVideoTimeoutKey = `recreateVideo_`;
const checkPlayVideoAfterStreamLoadTimeoutKey = `checkPlayVideoAfterStreamLoad_`;
const displayliveTimeoutKey = `videostream-displaylive_`;
const zoomTimeoutKey = `initalPanZoomTimeout_`;
const checkBlankWebRTCTimeoutKey = `checkBlankWebRTCTimeout_`;
const webRTCIceTimeoutKey = `webRTCIceTimeout_`;
const checkLiveIntervalKey = `checkLiveIntervalKey_`;
const playerIntervalKey = `playerIntervalKey_`;
const unwantedPauseTimeoutKey = `unwantedPauseTimeoutKey_`;
export default {
  name: 'VideoStream',
  components: {
    VideoArea,
    Slider,
    VideoControllers,
    CameraMenu,
  },
  props: {
    id: {
      type: Number,
      default: null,
    },
    streamConfig: {
      type: Object,
      default: () => ({
        accessKeyId: '',
        secretAccessKey: '',
        streamName: '',
        region: 'ap-northeast-1',
        refreshOnSessionChange: false,
      }),
    },
    meta: {
      type: Object,
      default: () => ({}),
    },
    size: {
      type: String,
      default: 'large',
    },
    draggable: {
      type: Boolean,
      default: false,
    },
    defaultLiveMode: {
      type: String,
      default: 'HLS',
    },
    live: {
      type: Boolean,
      default: true,
    },
    showAutoFocus: {
      type: Boolean,
      default: false,
    },
    showAbout: {
      type: Boolean,
      default: false,
    },
    showRightController: {
      type: Boolean,
      default: true,
    },
    showControllers: {
      type: Boolean,
      default: true,
    },
    showMotionAreas: {
      type: Boolean,
      default: false,
    },
    disableZoom: {
      type: Boolean,
      default: false,
    },
    playing: {
      type: Boolean,
      default: true,
    },
    hideTools: {
      type: Boolean,
      default: false,
    },
    main: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    controllerSize: {
      type: String,
      default: 'large',
    },
    playbackRate: {
      type: Number,
      default: 1,
    },
    hideDisplayController: {
      type: Boolean,
      default: false,
    },
    zoomoutOnRestore: {
      type: Boolean,
      default: true,
    },
    event: {
      type: Array,
      default: () => [],
    },
    bottomController: {
      type: Object,
      default: () => {
        return { mode: 'controller', selectMode: 'capture', selected: false };
      },
    },
    hideZoom: {
      type: Boolean,
      default: false,
    },
    hideStatus: {
      type: Boolean,
      default: false,
    },
    isAttach: {
      type: Boolean,
      default: false,
    },
    volumeOnRight: {
      type: Boolean,
      default: false,
    },
    controllerOnRight: {
      type: Boolean,
      default: false,
    },
    multipleArea: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      videoService: new VideoService(),
      rootSize: {
        width: 0,
        height: 0,
      },
      readyState: null,
      isDestroyed: false,
      prevTimestamp: 0,
      backwardGapTime: null,
      waitingForwardTimestamp: null,
      waitingBackwardTimestamp: null,
      waitingTimestampCallerId: null,
      lastCodecSequenceChange: null,
      currentVideoHole: null,
      hlsPlaylist: [],
      allFragmentHoles: [],
      fragmentCheckAbortController: null,
      fragmentIsPassingHole: false,
      endDiscontinuityTimeout: null,
      playerTimestamp: 0,
      playerTimer: new PerfectTimer('playerTimer', this.playbackRate),
      isDiscontinuity: false,
      isNoFragmentFound: false,
      isBuffering: true,
      currentFragmentTimestamp: 0,
      currentFragment: 0,
      oldVolume: 0,
      liveMode: 'HLS', // ['HLS', 'WebRTC']
      volume: 0,
      currentVideoPlayingMode: 'HLS',
      waitingForLiveModeStream: null,
      kvWebRTCService: null,
      signalingClientState: 3,
      iceState: 'New',
      kvService: new KVService(),
      reqId: null,
      appMode: process.env.VUE_APP_MODE,
      currentTimeIsChanging: false,
      cameraLoadingOnSnapshot: false,
      pauseAfterLoad: false,
      errorMessage: null,
      player: null,
      remoteView: null,
      urlVideo: null,
      protocol: 'HLS',
      fragmentSelectorType: 'PRODUCER_TIMESTAMP',
      startTimestamp: dayjs().subtract(1, 's').valueOf(),
      endTimestamp: dayjs().valueOf(),
      containerFormat: 'MPEG_TS',
      discontinuityMode: 'ON_DISCONTINUITY',
      displayFragmentTimestamp: 'ALWAYS',
      noVideoLoading: false,
      expires: 3600,
      maxResultsOnDemand: 4000,
      showAccess: false,
      showSecret: false,
      loading: true,
      showVideo: true,
      cameraIndex: 0,
      signal: 1, // {1: sync, 2: wifi-off, 3: wifi}
      cameraStatus: CameraStatus.Loading,
      displayControllerVisible: false,
      fullscreen: false,
      timestamp: undefined,
      zoom: 10,
      maxZoom: 100,
      isDraggingAction: false,
      isDraggingVideo: false,
      draggingVideoStartOffset: [],
      draggingVideoDeltaOffset: [0, 0],
      droppedVideoDeltaOffset: [0, 0],
      panzoomInstance: null,
      videoCanvas: document.createElement('canvas'),
      videoContext: null,
      resizeObserver: null,
      videoMetadata: {
        w: 0,
        h: 0,
        ratio: 0,
      },
      poster: '/img/skeleton.png',
      isSnapshotBlank: false,
      kinesisVideoError: false,
      speakerOn: false,
      observer: null,
    };
  },
  computed: {
    ...mapState({
      testMode: (state) => state.common.testMode,
      cameraList: (state) => state.common.cameraList,
      networkOnline: (state) => state.common.networkOnline,
    }),
    showLoadingPoster() {
      return this.isSnapshotBlank && +this.readyState !== 4;
    },
    waitingForData() {
      return this.waitingBackwardTimestamp || this.waitingForwardTimestamp;
    },
    firstFragment() {
      return this.hlsPlaylist?.[0] || null;
    },
    currentFragTime() {
      return dayjs(this.currentFragmentTimestamp).format('HH:mm:ss');
    },
    overlapHoles() {
      return this.allFragmentHoles.filter(
        (hole) => hole.type === FragmentHoleType.SkipOverlap
      );
    },
    videoOffHoleList() {
      return this.allFragmentHoles.filter(
        (hole) => hole.type === FragmentHoleType.VideoOff
      );
    },
    isTimestampInBackwardGap() {
      return (
        this.backwardGapTime &&
        this.timestamp < dayjs(this.backwardGapTime.programDateTime).valueOf()
      );
    },
    currentCamera() {
      return this.cameraList.find((camera) => camera.id === this.id);
    },
    showDisplayController() {
      return !this.hideDisplayController && this.displayControllerVisible;
    },
    playbackMode() {
      return this.live ? 'LIVE' : 'ON_DEMAND';
    },
    canAutoPlay() {
      return this.liveMode === 'WebRTC' || this.isSafari;
    },
    isPlaying: {
      get() {
        return this.playing;
      },
      set(val) {
        this.$emit('update:playing', val);
      },
    },
    isLive: {
      get() {
        return this.live;
      },
      set(val) {
        this.$emit('update:live', val);
      },
    },
    playbackRateValue: {
      get() {
        return this.playbackRate;
      },
      set(val) {
        this.$emit('update:playbackRate', val);
      },
    },
    isSafari() {
      return this.$device.isSafari;
    },
    signalingClientStateText() {
      switch (this.signalingClientState) {
        case 0:
          return 'Connecting';
        case 1:
          return 'Open';
        case 2:
          return 'Closing';
        case 3:
          return 'Closed';
        default:
          return 'Unknown';
      }
    },
    remoteViewRef() {
      return this.liveMode === 'WebRTC' && this.isLive
        ? this.$refs.remoteViewWebRTC
        : this.$refs.remoteView;
    },
    videoControllerMode() {
      return this.errorMessage ? 'controller' : this.bottomController.mode;
    },
    recreateVideoTimeout() {
      return recreateVideoTimeoutKey + this.id;
    },
    checkPlayVideoAfterStreamLoadTimeout() {
      return checkPlayVideoAfterStreamLoadTimeoutKey + this.id;
    },
    displayliveTimeout() {
      return displayliveTimeoutKey + this.id;
    },
    zoomTimeout() {
      return zoomTimeoutKey + this.id;
    },
    checkBlankWebRTCTimeout() {
      return checkBlankWebRTCTimeoutKey + this.id;
    },
    webRTCIceTimeout() {
      return webRTCIceTimeoutKey + this.id;
    },
    checkLiveInterval() {
      return checkLiveIntervalKey + this.id;
    },
    playerInterval() {
      return playerIntervalKey + this.id;
    },
    unwantedPauseTimeout() {
      return unwantedPauseTimeoutKey + this.id;
    },
  },
  watch: {
    isTimestampInBackwardGap(newVal, oldVal) {
      if (newVal !== oldVal && this.backwardGapTime && this.isDiscontinuity) {
        if (!newVal) {
          this.handleGoToTimeOutOfGapTime({
            startTimestamp: dayjs(
              this.backwardGapTime.programDateTime
            ).valueOf(),
          });
        }
      }
    },
    networkOnline(newValue) {
      this.handleNeworkStatusChange(newValue);
    },
    'currentCamera.sessionToken': {
      handler: function (token) {
        if (this.streamConfig.refreshOnSessionChange) {
          this.handleSessionTokenChange(token);
        }
      },
    },
    playbackRate(val) {
      if (this.playerTimer) {
        this.playerTimer.setSpeed(val);
      }
      this.remoteViewRef.playbackRate = val;
    },
    isPlaying(val) {
      const video = this.remoteViewRef;
      if (!video) {
        return;
      }

      // when pause/play in the gap between two fragments caused by `no fragment found error from KVS`
      if (
        this.isNoFragmentFound ||
        this.cameraStatus === PlayerStatus.OutOfRetentionPeriod ||
        this.isDiscontinuity
      ) {
        if (val) {
          this.playerTimer.start();
        } else {
          this.playerTimer.stop();
        }
        return;
      }
      if (val) {
        video.play().catch(() => {});
      } else {
        var playPromise = video.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Automatic playback started!
              // Show playing UI.
              video.pause();
            })
            .catch((error) => {
              // Auto-play was prevented
              // Show paused UI.
              console.log(error);
            });
        }
      }
    },
    cameraStatus(val) {
      const index = this.cameraList.findIndex((cam) => cam.id === this.id);
      this.$emit('onCameraStatus', { status: val, id: this.id, index });
    },
    id: {
      handler: function (newVal, oldVal) {
        for (var i = 0; i < this.cameraList.length; i++) {
          if (this.cameraList.id === newVal) {
            this.cameraIndex = i;
            break;
          }
        }
        if (oldVal !== undefined && oldVal !== null) {
          this.clearAllTimeout(oldVal);
          this.$nextTick(() => {
            this.playOnMount();
          });
        }
      },
      immediate: true,
    },
    liveMode() {
      clearTimeout(window[this.webRTCIceTimeout]);
      clearTimeout(window[this.checkBlankWebRTCTimeout]);
    },
    waitingForData(val, oldVal) {
      if (val !== oldVal) {
        this.$emit('onWaiting', {
          id: this.currentCamera.id,
          status: val,
        });
      }
    },
  },
  created() {
    EventBus.on(EventBusEvents.CallVideoStream, this.callVideoStreamListener);
    EventBus.on(
      EventBusEvents.AuthFailureCleanup,
      this.handleAuthFailureCleanup
    );
    this.debounceResize = debounce(this.onResize, 1);
    this.handleLoadMetadata = this.handleLoadMetadata.bind(this);
    this.startTimestamp =
      this.streamConfig.startTimestamp || this.startTimestamp;
    this.endTimestamp = this.streamConfig.endTimestamp || this.endTimestamp;

    this.liveMode = !this.currentCamera.webRTCChannelName
      ? null
      : this.defaultLiveMode;
  },
  mounted() {
    this.isDestroyed = false;
    this.liveMode = this.currentCamera.latencyMode;
    this.currentVideoPlayingMode = this.currentCamera.latencyMode;
    this.checkCameraUpdateState(this.currentCamera);
    this.resizeObserver = new ResizeObserver(this.debounceResize).observe(
      this.$refs.root
    );
    this.$emit('onAction', {
      type: 'mounted',
      payload: {
        id: this.currentCamera.id,
        streamConfig: this.streamConfig,
        event: this.event,
      },
    });
    window.addEventListener('keyup', this.handleEsc);
    this.remoteView = this.remoteViewRef;
    if (this.testMode) {
      this.checkVideoFrameRate(this.$refs.remoteView);
    }
    if (this.streamConfig?.connectOnPresence) {
      this.observeVisibility();
    } else {
      this.playOnMount();
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect(); // disconnect observer on component destroy
    }
    this.isDestroyed = true;
    this.stopTracking(); // ensure animation is stopped before component destruction
    this.$emit('onAction', {
      type: 'beforeDestroy',
      payload: {
        id: this.currentCamera?.id,
      },
    });
    this.clearAllTimeout(this.id);
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.$refs.root);
    }
    if (this.panzoomInstance) {
      this.panzoomInstance.dispose();
    }
    window.removeEventListener('keyup', this.handleEsc);
    EventBus.off(EventBusEvents.CallVideoStream, this.callVideoStreamListener);
    EventBus.off(
      EventBusEvents.AuthFailureCleanup,
      this.handleAuthFailureCleanup
    );
    this.disconnectCamera();

    const timeoutId = `videostream-displaylive-${this.id}`;
    clearTimeout(timeoutId);
  },
  methods: {
    clearAllTimeout(id) {
      clearTimeout(window[webRTCIceTimeoutKey + id]);
      clearInterval(window[checkLiveIntervalKey + id]);
      clearInterval(window[playerIntervalKey + id]);
      clearTimeout(window[checkBlankWebRTCTimeoutKey + id]);
      clearTimeout(window[recreateVideoTimeoutKey + id]);
      clearTimeout(window[checkPlayVideoAfterStreamLoadTimeoutKey + id]);
      clearTimeout(window[displayliveTimeoutKey + id]);
      clearTimeout(window[zoomTimeoutKey + id]);
      clearTimeout(window[unwantedPauseTimeoutKey + id]);

      endDiscontinuity(this, { callerPath: 'methods/clearAllTimeout' });
      this.clearPlayerTimer();
      this.clearEndDiscontinuityTimeout();
    },
    connectCamera() {
      if (this.liveMode === 'WebRTC' && this.isLive) {
        this.tryPlayingWebRTC();
      } else {
        this.currentFragmentTimestamp = this.startTimestamp + 0;

        this.playHLSVideo({
          startTimestamp: this.startTimestamp,
          endTimestamp: checkHLSEndTimestamp(
            this.startTimestamp,
            this.endTimestamp
          ),
          playbackMode: this.isLive ? 'LIVE' : 'ON_DEMAND',
        });
      }
    },
    disconnectCamera() {
      if (this.timestamp) {
        this.startTimestamp = +this.timestamp;
      }
      const disposeReason = this.currentCamera?.name
        ? `[${this.currentCamera.name}] destroyed`
        : '[Unknown Camera] destroyed';
      this.disposeKVS(disposeReason);
      this.stopWebRTC();
    },
    observeVisibility() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.connectCamera();
          } else {
            this.disconnectCamera();
          }
        });
      });
      this.observer.observe(this.$el); // observe the root element of this component
    },
    updateReadyState() {
      if (!this.remoteViewRef) {
        return;
      }
      this.readyState = this.remoteViewRef.readyState;
    },
    handleCanPlay() {
      this.updateReadyState();
    },
    handleTimeUpdate() {
      this.updateReadyState();
    },
    handleCameraLoading() {
      this.$emit('onLoading', { id: this.currentCamera.id });
    },
    handleSeeking() {
      this.$emit('onSeeking', { id: this.currentCamera.id });
    },
    getCpdSequence() {
      return this.hlsPlaylist.find((f) => f.codecChanged)?.cc;
    },
    handleSeeked() {},
    getFragmentCheckSignal() {
      if (
        this.fragmentCheckAbortController &&
        !this.fragmentCheckAbortController?.signal?.aborted
      ) {
        this.fragmentCheckAbortController.abort(
          new DOMException(
            'Aborted fragment check for new request',
            'AbortError'
          )
        );
      }
      this.fragmentCheckAbortController = new AbortController();
      return this.fragmentCheckAbortController.signal;
    },
    prepareForFragmentChange({
      startTimestamp,
      endTimestamp,
      pauseAfterLoad,
      live,
    }) {
      this.kvService.stopLoad();
      clearTimeout(window[this.checkPlayVideoAfterStreamLoadTimeout]);
      this.currentTimeIsChanging = true;
      if (this.isDiscontinuity) {
        resetDiscontinuity(this);
      }
      this.isNoFragmentFound = false;
      this.clearPlayerTimer();
      this.pauseAfterLoad = pauseAfterLoad;
      this.startTimestamp = startTimestamp;
      this.endTimestamp = checkHLSEndTimestamp(startTimestamp, endTimestamp);
      this.isLive = live;
      if (live) {
        this.playbackRateValue = 1;
      }
    },
    handleGoToTimeFallInGapTime({ startTimestamp, videoOffHole }) {
      this.setWaitingForNewTimestamp({
        newTimestamp: startTimestamp,
        callerId: 'seek:discontinuity',
      });
      const pauseTimeBeforeGap = null;
      this.timestamp = startTimestamp + 0;
      this.currentFragmentTimestamp = startTimestamp;
      this.pausePlayerOnVideoOff(pauseTimeBeforeGap, videoOffHole);
    },
    async handleGoToTimeOutOfGapTime({ startTimestamp }) {
      if (this.backwardGapTime) {
        this.changeFragmentTime({
          direction: 'forward',
          startTimestamp: startTimestamp,
          pauseAfterLoad: !this.isPlaying,
          live: false,
          callerPath: 'seek/isDiscontinuity/!isInGapTime',
        });
      } else {
        const videoOffHole = { ...this.currentVideoHole };
        if (!videoOffHole) {
          console.warn(
            '[WARN] There is no video hole, cannot handle the discontinuity'
          );
          return;
        }

        // check if the requested timestamp is in any fragment
        if (this.hlsPlaylist.length === 0) {
          return;
        }
        // get exact fragment and vide.currentTime that the requested timestamp is in
        const timeInFragment = getFragmentByTimestamp(
          this.hlsPlaylist,
          startTimestamp
        );

        // if the time between the start of the video hole and the requested timestamp is greater than 5 seconds, then request for new playlist
        // or if the requested timestamp is not in any fragment, then request for new playlist
        // request for new playlist called in this condition to make sure the video is play if any possible error in fragment
        if (!timeInFragment) {
          // change the fragment will adjust the video time to move the video to the requested timestamp
          // change the fragment will reset the discontinuity state
          this.changeFragmentTime({
            startTimestamp: startTimestamp,
            live: false,
            callerPath: 'seek/isDiscontinuity/!isInGapTime',
          });
        } else {
          const videoTime = timeInFragment.videoCurrentTime;
          // end the discontinuity will adjust the video time to move the video to the end of the video hole
          await endDiscontinuity(this, {
            videoTime,
            startTimestamp,
          });
        }
      }
    },
    handleForwardInDiscontinuity({ startTimestamp, value, isInGapTime }) {
      if (isInGapTime) {
        this.playerTimer.addTimeElapsed(value);
      } else {
        // if user timestamp is out of the gap time, then it must be after the gap time as user is moving seekbar forward
        this.handleGoToTimeOutOfGapTime({
          startTimestamp,
        });
      }
    },
    resetPlayerTimerWithNewTimestamp(timestamp) {
      tracer('resetPlayerTimerWithNewTimestamp', {
        timestamp: dayjs(timestamp).format('HH:mm:ss'),
      });
      this.currentFragmentTimestamp = timestamp;

      this.playerTimer.reset();
      if (this.isPlaying) {
        this.startPlayerTimer();
      }
    },
    checkPlaylistEnded(timestamp) {
      const waitingTimestamp =
        this.waitingBackwardTimestamp || this.waitingForwardTimestamp;
      const lastFrag =
        this.hlsPlaylist?.length > 0 ? this.hlsPlaylist.at(-1) : null;
      if (
        lastFrag &&
        !this.currentTimeIsChanging &&
        !waitingTimestamp &&
        !this.isLive
      ) {
        const lastFragDayJs = dayjs(lastFrag.programDateTime);
        const timestamDayJs = dayjs(timestamp);
        return (
          lastFragDayJs.isBefore(timestamDayJs) &&
          lastFragDayJs
            .add(1 + lastFrag.duration, 'second')
            .isAfter(timestamDayJs)
        );
      }
      return false;
    },
    handleSessionTokenChange() {
      this.handleRefresh(true);
    },
    handleStreamingSessionExpired() {
      VideoStreamPrint.warnPlayerLoadLevelExpired({
        cameraName: this.currentCamera.name,
        timestamp: this.timestamp,
      });
      const disposeReason = `[${this.currentCamera.name}] streaming session expired`;
      this.disposeKVS(disposeReason);
      this.emitError(
        PlayerStatus.StreamingSessionExpired,
        new Error('Streaming session token is expired.')
      );
    },
    takePreviewSnapshot(noLoading, video) {
      if (
        noLoading &&
        !this.cameraLoadingOnSnapshot &&
        video.readyState > 0 &&
        video.currentTime > 0
      ) {
        this.cameraLoadingOnSnapshot = true;
        this.poster = this.snap().toDataURL();
      }
    },
    checkRetentionPeriod(startTimestamp) {
      const retentionPeriodTime = dayjs().subtract(
        this.currentCamera.retentionPeriod,
        'hours'
      );
      const isOutOfRetentionPeriod =
        dayjs(startTimestamp).isBefore(retentionPeriodTime);

      if (isOutOfRetentionPeriod) {
        const disposeReason = `[${this.currentCamera.name}] out of retention period`;
        this.disposeKVS(disposeReason);
        this.stopWebRTC();
        this.errorMessage = {
          text: '録画映像の保存期間外',
          theme: 'dark-bg',
        };

        this.signal = 2;
        this.handleCameraStatus(PlayerStatus.OutOfRetentionPeriod);
        this.resetPlayerTimerWithNewTimestamp(startTimestamp);
        this.clearWaitingForNewTimestamp();
      }
      return isOutOfRetentionPeriod;
    },
    handleNoFragmentChange() {
      this.errorMessage = VideoStreamErrorMessages.LiveNoFragmentFound;
      console.warn(
        '[WARN] Seems there is no fragment to display. There was no fragment change for a while. We continue sending request in the background to fetch the data if any.'
      );
      this.resetPlayerTimerWithNewTimestamp(dayjs().valueOf());
      this.clearWaitingForNewTimestamp();
    },
    isUnwantedPause(target) {
      return (
        this.cameraStatus === CameraStatus.Connected &&
        target.paused &&
        this.isPlaying &&
        target.readyState === 4 &&
        !this.isNoFragmentFound &&
        !this.isDiscontinuity
      );
    },
    handleVideoEnded(e) {
      if (this.checkPlaylistEnded(this.timestamp)) {
        const lastFrag = this.hlsPlaylist.at(-1);
        VideoStreamPrint.warnPlaylistEnding({
          cameraName: this.currentCamera.name,
          timestamp: this.timestamp,
          lastFrag,
        });
        const lastFragEnd = dayjs(lastFrag.programDateTime)
          .add(lastFrag.duration, 'seconds')
          .valueOf();
        if (isTimeCloseToLive(lastFragEnd)) {
          console.warn(
            `The the timestamp is close to the LIVE stream, it automatically switches to the LIVE stream. The time difference is ${dayjs
              .duration(
                dayjs().diff(
                  dayjs(lastFrag.programDateTime).add(
                    lastFrag.duration,
                    'seconds'
                  )
                )
              )
              .asSeconds()} seconds. `
          );
          this.$emit('onAction', {
            type: 'switchLive',
            payload: this.currentCamera,
          });
        } else {
          this.startTimestamp = this.timestamp;
          this.handleRefresh(true, true);
        }
        return;
      }

      const { target } = e;
      if (target) {
        return;
      }
      VideoStreamPrint.warnVideoEnded({
        cameraName: this.currentCamera.name,
        cameraStatus: this.cameraStatus,
        readyState: target.readyState,
        isNoFragmentFound: this.isNoFragmentFound,
        isDiscontinuity: this.isDiscontinuity,
        timestamp: this.timestamp,
        isPlaying: this.isPlaying,
      });
      if (this.isUnwantedPause(target)) {
        VideoStreamPrint.warnDetectUnwantedPause({
          cameraName: this.currentCamera.name,
          timestamp: this.timestamp,
        });

        this.handleUnwantedPause(target);
      }
    },
    handleUnwantedPause(target) {
      clearTimeout(window[this.unwantedPauseTimeout]);
      window[this.unwantedPauseTimeout] = undefined;

      window[this.unwantedPauseTimeout] = setTimeout(() => {
        if (this.isUnwantedPause(target)) {
          this.playPlayer({ target });

          VideoStreamPrint.warnPlayUnwantedPause({
            cameraName: this.currentCamera.name,
            timestamp: this.timestamp,
          });
        }
        window[this.unwantedPauseTimeout] = undefined;
      }, Unwanted_Pause_Timeout_Duration_Ms); // wait for `Unwanted_Pause_Timeout_Duration_Ms` then if the video playing status is not changed, will call play function
    },
    handleNoData() {
      this.stopHLSVideo();
      this.kinesisVideoError = true;
      this.signal = 2;
      this.signal;
      this.errorMessage = {
        text: '映像の再生に失敗しました。接続状態を確認して、もう一度お試しください。',
        theme: 'dark-bg',
      };
      this.handleCameraStatus(CameraStatus.NoData);
    },
    clearEndDiscontinuityTimeout() {
      clearTimeout(this.endDiscontinuityTimeout);
    },
    handleFragmentBuffered(frag) {
      if (window[this.unwantedPauseTimeout]) {
        return;
      }
      const video = this.remoteViewRef || this.remoteView;
      if (
        this.isUnwantedPause(video) &&
        frag.programDateTime > this.timestamp
      ) {
        VideoStreamPrint.warnVideoEnded({
          cameraName: this.currentCamera.name,
          paused: video.paused,
          cameraStatus: this.cameraStatus,
          readyState: video.readyState,
          isNoFragmentFound: this.isNoFragmentFound,
          isDiscontinuity: this.isDiscontinuity,
          timestamp: this.timestamp,
          isPlaying: this.isPlaying,
        });
        VideoStreamPrint.warnDetectUnwantedPause({
          cameraName: this.currentCamera.name,
          timestamp: this.timestamp,
          fragTimestamp: frag.programDateTime,
        });
        this.handleUnwantedPause(video);
      }
    },
    handleKVSCodecIsNotTheSame() {
      if (this.isLive) {
        this.handleKVSCodecIsNotTheSameLive();
      }
    },
    handleKVSCodecIsNotTheSameLive() {
      // TODO: need clarificaiton
      const disposeReason = `[${this.currentCamera.name}] codec changed`;
      this.disposeKVS(disposeReason);
      console.warn(
        `[WARN][${dayjs().format(
          'HH:mm:ss'
        )}] The requested fragment does not have the same codec private data as the rest of the session.`
      );

      this.handleRenewOnCodecChange();
    },
    /**
     * Deprecated
     * VOD CPD change will be detected on on Level Loaded event of hls.js
     * @param {*} param0
     */
    handleKVSCodecIsNotTheSameVOD({ frag }) {
      const startTimestamp = dayjs(frag.programDateTime).valueOf();
      console.warn(
        `[WARN] The requested fragment does not have the same codec private data as the rest of the session. We renew session at ${dayjs(
          startTimestamp
        ).format('HH:mm:ss')}`
      );
      this.$set(this.hlsPlaylist[frag.sn - 1], 'codecChanged', true);
    },
    handleRenewOnCodecChange(startTimestamp) {
      if (this.isLive) {
        tracer('[video-stream]/handleRenewOnCodecChange/isLive', {
          startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
        });
        this.timestamp = getHLSLiveStreamStartTimestamp();
        this.displayLive();
      } else {
        tracer('[video-stream]/handleRenewOnCodecChange/isVOD', {
          startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
        });
        this.changeFragmentTime({
          startTimestamp: startTimestamp,
          endTimestamp: checkHLSEndTimestamp(startTimestamp, this.endTimestamp),
          live: false,
          pauseAfterLoad: !this.isPlaying,
          noLoading: true,
          callerPath: 'checkCodecChangeTimestamp/codecChangeFrag',
        });
      }
    },
    handleCanNotRecover(frag) {
      const video = this.remoteViewRef;
      let currentFrag = findCurrentSegment(this.hlsPlaylist, video);
      let nextFrag = null;
      if (currentFrag) {
        currentFrag = this.hlsPlaylist[currentFrag.sn];
      } else if (
        !currentFrag &&
        parseInt(video.currentTime) < 2 &&
        this.hlsPlaylist.length > 0
      ) {
        nextFrag = this.hlsPlaylist[1];
      }

      const fragErr = frag || nextFrag;
      if (!this.isLive && !fragErr) {
        console.warn(
          `[WARN] can not handle revocery stream when fragment is null/undefined in VOD.
          is Live: ${this.isLive}
          fragErr:
          `,
          fragErr
        );
      }

      const liveSkipTimestamp = dayjs().subtract(500, 'milliseconds').valueOf();
      const vodSkipTimestampUnknownIssue = this.timestamp + 5000; // it will be adjusted more accurate in the case the unkown issue detected.
      const vodSkipTimestamp = fragErr
        ? dayjs(fragErr.programDateTime)
            .add(fragErr.duration + 0.1, 'seconds')
            .valueOf()
        : vodSkipTimestampUnknownIssue;

      // skip current fragment when can not handle media recovery using hls.js
      const startTimestamp = this.isLive ? liveSkipTimestamp : vodSkipTimestamp;
      console.warn(
        '[WARN] can not revocer the stream, timestamp moves forward to ' +
          dayjs(startTimestamp).format('HH:mm:ss')
      );
      // reset timestamp forwarding
      this.resetPlayerTimerWithNewTimestamp(startTimestamp);
      this.timestamp = startTimestamp;
      this.changeFragmentTime({
        startTimestamp: startTimestamp,
        endTimestamp: checkHLSEndTimestamp(startTimestamp, this.endTimestamp),
        live: this.isLive,
        pauseAfterLoad: false,
        callerPath: 'handleCanNotRecover',
      });
    },
    startCheckLiveInterval() {
      clearInterval(window[this.checkLiveInterval]);
      window[this.checkLiveInterval] = setInterval(this.checkLive, 11800);
    },
    clearWaitingForNewTimestamp() {
      this.waitingForwardTimestamp = null;
      this.waitingBackwardTimestamp = null;
      this.waitingTimestampCallerId = null;
    },

    /**
     * Handle auth failure cleanup event - dispose all KVS resources
     */
    handleAuthFailureCleanup(eventData) {
      // stop all renewal and retry attempts immediately
      this.clearAllTimeout(this.id);

      // dispose all KVS resources
      const disposeReason = `Token expired - ${eventData.reason}`;
      this.disposeKVS(disposeReason);

      // stop WebRTC if active
      this.stopWebRTC();

      // clear error state and set no data state
      this.errorMessage = {
        text: ' ',
        theme: 'dark-bg',
      };
      this.kinesisVideoError = true;
      this.handleCameraStatus(CameraStatus.NoData);
    },
    setWaitingForNewTimestamp({
      direction,
      newTimestamp,
      live,
      callerId,
      prevTimestamp,
    }) {
      // when end-user request for new timestamp we will change the state to waiting for new timestamp
      // in this state we do not update seekbar timestamp until we reach the point of requested timestamp by end-user
      this.prevTimestamp = (prevTimestamp || this.timestamp || 0) + 0;
      this.waitingBackwardTimestamp = null;
      this.waitingForwardTimestamp = null;
      this.waitingTimestampCallerId = callerId;
      if (
        direction === 'backward' ||
        (newTimestamp < this.timestamp && direction === undefined && !live)
      ) {
        this.waitingBackwardTimestamp = newTimestamp + 0;
      } else {
        this.waitingForwardTimestamp = live
          ? dayjs(newTimestamp).subtract(6, 'seconds')
          : newTimestamp + 0;
      }
    },
    checkLive() {
      if (
        this.live &&
        this.checkVideoPlaying() &&
        !this.isNoFragmentFound &&
        !this.isDiscontinuity &&
        !this.waitingForLiveModeStream &&
        this.liveMode !== 'WebRTC' &&
        !this.currentTimeIsChanging &&
        dayjs(this.timestamp).add(20, 'seconds').valueOf() < dayjs().valueOf()
      ) {
        VideoStreamPrint.warnReloadLiveOnCheckLive({
          cameraName: this.currentCamera.name,
          timestamp: this.timestamp,
        });
        this.displayLive(true);
      }
    },
    playPlayer({ target }) {
      const promise = target.play();
      if (promise !== undefined) {
        promise.catch((e) => {
          if (e.name === 'NotAllowedError' || e.name === 'NotSupportedError') {
            this.handleNotAllowVideoPlay();
          }
        });
      }
    },
    pausePlayer({ target, afterPause }) {
      const video = target || this.remoteViewRef;
      if (!video) {
        console.warn('[WARN] video element does not exist.');
        return;
      }
      var playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Automatic playback started!
            // Show playing UI.
            video.pause();
            if (afterPause) {
              afterPause();
            }
          })
          .catch((error) => {
            // Auto-play was prevented
            // Show paused UI.
            console.log(error);
          });
      }
    },
    playOnMount() {
      if (this.streamConfig.autoplay) {
        this.connectCamera();
      }
    },
    exitPictureInPicture() {
      // https://developer.mozilla.org/en-US/docs/Web/API/Document/exitPictureInPicture
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch((err) => console.error(err));
      }
    },
    handleFragmentLoaded() {},
    handleFragmentChanged({ frag }) {
      // VideoStreamPrint.logVideoStreamFragmentChange(this);
      // everytime fragment changes we need to reset player timer to increase time from zero adding on new fragment `programDateTime`
      this.playerTimer.reset();
      this.currentFragment = frag;
      this.currentFragmentTimestamp = dayjs(frag.programDateTime).valueOf();
    },
    clearPlayerTimer() {
      clearInterval(window[this.playerInterval]);
      if (this.playerTimer) {
        this.playerTimer.reset();
        this.playerTimer.stop();
      }
    },
    pausePlayerTimer() {
      if (this.playerTimer) {
        this.playerTimer.stop();
      }
    },
    startPlayerTimer() {
      this.playerTimer.start();

      clearInterval(window[this.playerInterval]);

      window[this.playerInterval] = setInterval(() => {
        const timeInMsSeconds = this.playerTimer.getTime();
        const newTimestamp = dayjs(this.currentFragmentTimestamp).add(
          timeInMsSeconds,
          'milliseconds'
        );
        this.playerTimestamp = newTimestamp.format('HH:mm:ss:SSS');

        this.updateCurrentTimestamp(newTimestamp.valueOf());
      }, 100);
    },
    checkVideoPlaying() {
      const video = this.remoteViewRef || this.remoteView;
      return !!(
        video?.currentTime > 0 &&
        !video?.paused &&
        !video?.ended &&
        video?.readyState > 2
      );
    },
    setVolume(val) {
      this.volume = val === 0 ? 0 : this.oldVolume || 1;
      this.remoteViewRef.volume = this.volume / 10;
    },
    checkCameraUpdateState(cam) {
      let canPlay = true;
      if (cam.UpdateVersion && cam.UpdateVersion.length) {
        if (
          cam.UpdateVersion.some(
            (u) =>
              u.updatingState === 1 &&
              ['FIRMWARE', 'ArobaView RA'].includes(u.name)
          )
        ) {
          this.errorMessage = {
            text: `ライブカメラのバージョンを更新中です。<br>
更新が終わるまでお待ちください。`,
          };
          canPlay = false;
          this.endLoading(true);
          this.signal = 2;
          this.handleCameraStatus(CameraStatus.Error);
        } else if (cam.UpdateVersion[0].updatingState === 3) {
          this.errorMessage = {
            text: `カメラのバージョン更新に失敗しました。<br>
再試行してください。`,
          };
          canPlay = false;
          this.endLoading(true);
          this.signal = 2;
          this.handleCameraStatus(CameraStatus.Error);
        } else if (!this.kinesisVideoError) {
          this.errorMessage = null;
        }
      }
      return { canPlay };
    },
    checkWebRTCIceTimeout() {
      if (window[this.webRTCIceTimeout]) {
        clearTimeout(window[this.webRTCIceTimeout]);
      }

      window[this.webRTCIceTimeout] = setTimeout(() => {
        if (this.signal !== 3) {
          console.warn('[WebRTC] Ice timeout detected switching to HLS mode.');
          this.$emit('onCameraStatus', { code: 'icetimeout' });
          EventBus.emit(EventBusEvents.VideoStreamErrorOpen, true);
          this.switchLiveMode('HLS');
          this.$emit('onLowLatencyError', null);
        }
      }, 7000); // required time from loading to change ICE state
    },
    checkBlankWebRTC() {
      if (window[this.checkBlankWebRTCTimeout]) {
        clearTimeout(window[this.checkBlankWebRTCTimeout]);
      }
      window[this.checkBlankWebRTCTimeout] = setTimeout(() => {
        if (this.signal !== 3) {
          console.warn('[WebRTC] Blank stream detected switching to HLS mode.');
          this.$emit('onCameraStatus', { code: 'blankstream' });
          EventBus.emit(EventBusEvents.VideoStreamErrorOpen, true);
          this.switchLiveMode('HLS');
          this.$emit('onLowLatencyError', null);
        }
      }, 6000);
    },
    async tryPlayingWebRTC() {
      try {
        await this.playWebRTC();
      } catch (error) {
        EventBus.emit(EventBusEvents.VideoStreamErrorOpen, true);
        console.error(
          '[WebRTC] Error detected, it will switch to HLS mode.',
          error
        );
        setTimeout(() => {
          this.switchLiveMode('HLS');
          this.$emit('onLowLatencyError', null);
        }, 20); // When user set low latancy mode and it fails it will switch to HLS mode. But user click on switch will occurs after switch to HLS mode(it has delay!)
        // As a result we have -> user click on switch -> WebRTC fails -> switch to HLS mode -> switch to WebRTC mode on user click(issue!!)
        // To solve this issue we set a timeout 20ms.
        // As a result we have -> user click on switch ->  switch to WebRTC mode on user click -> switch to HLS mode
        // 20ms small enough to work for this case
      }
    },
    checkVideoFrameRate(video) {
      const fpsInfo = document.querySelector('#fps-info');
      const metadataInfo = document.querySelector('#metadata-info');

      let paintCount = 0;
      let startTime = 0.0;

      const update = (now, metadata) => {
        if (startTime === 0.0) {
          startTime = now;
        }
        const elapsed = (now - startTime) / 1000.0;
        const fps = (++paintCount / elapsed).toFixed(3);
        fpsInfo.innerText = `video fps: ${fps}`;
        metadataInfo.innerText = JSON.stringify(metadata, null, 2);

        video.requestVideoFrameCallback(update);
      };

      video.requestVideoFrameCallback(update);
    },
    handleVolume(level) {
      this.remoteViewRef.volume = level / 10;
      this.oldVolume = level;
      this.volume = level;
    },
    handleAutofocus() {
      this.autofocus(this.id);
    },
    handleMuted(status) {
      this.speakerOn = status;
      this.$emit('onSpeakerOn', status);
    },
    handleNotAllowVideoPlay() {
      console.log(
        '[WARN] Video is not allowed to play with current settings, we will try to play it anyway switching to WebRTC mode'
      );
      this.switchLiveMode('WebRTC');
    },
    onResize() {
      if (this.$refs.root) {
        this.rootSize = {
          width: this.$refs.root.offsetWidth,
          height: this.$refs.root.offsetHeight,
        };
      }
    },
    handleCanPlayThroughHLS() {
      this.currentVideoPlayingMode = 'HLS';
      if (this.waitingForLiveModeStream !== 'WebRTC') {
        this.waitingForLiveModeStream = null;
        this.isSnapshotBlank = false;
        this.stopWebRTC();
      }
    },
    handleCanPlayThroughWebRTC() {
      this.currentVideoPlayingMode = 'WebRTC';
      if (this.waitingForLiveModeStream === 'WebRTC') {
        this.waitingForLiveModeStream = null;
        const disposeReason = `[${this.currentCamera.name}] switched to WebRTC`;
        this.disposeKVS(disposeReason);
      }
      this.signal = 3;
    },
    recreateVideo() {
      if (window[this.recreateVideoTimeout]) {
        clearTimeout(window[this.recreateVideoTimeout]);
      }
      return new Promise((resolve) => {
        this.showVideo = false;
        window[this.recreateVideoTimeout] = setTimeout(() => {
          this.showVideo = true;
          resolve();
        }, 500);
      });
    },
    async handleLiveMode(mode) {
      await this.setLowLatencyModeById({ id: this.id, latencyMode: mode });
      this.$emit('onLiveMode', { mode, cameraId: this.id });
      await this.switchLiveMode(mode);
    },
    async switchLiveMode(mode) {
      this.liveMode = mode;
      if (this.isLive) {
        this.signal = 1;
        this.waitingForLiveModeStream = mode;
        if (mode === 'WebRTC') {
          await this.tryPlayingWebRTC();
        } else {
          this.displayLive();
        }
      }
      await this.setLowLatencyModeById({ id: this.id, latencyMode: mode });
    },
    playWebRTC() {
      if (['New', 'Disconnected'].includes(this.iceState) === false) {
        console.warn(
          '[WebRTC] can not play WebRTC because current ICE state is not [New|Disconnected]. Current ICE state: ' +
            this.iceState
        );
        return;
      }

      const { canPlay } = this.checkCameraUpdateState(this.currentCamera);
      if (!canPlay) {
        return;
      }
      this.errorMessage = null;
      const localView = this.$refs.localView;
      const remoteView = this.remoteViewRef;
      if (!this.kvWebRTCService) {
        this.kvWebRTCService = new KVWebRTCService();
      }
      const config = {
        region: this.currentCamera.awsRegionName,
        channelName: this.currentCamera.webRTCChannelName,
        clientId: getRandomId(),
        sendVideo: false,
        sendAudio: false,
        openDataChannel: false,
        widescreen: this.appMode === 'production',
        fullscreen: false,
        useTrickleICE: true,
        natTraversalDisabled: false,
        forceTURN: false,
        accessKeyId: this.streamConfig.accessKeyId,
        endpoint: null,
        secretAccessKey: this.streamConfig.secretAccessKey,
        sessionToken: null,
      };
      return new Promise((resolve, reject) => {
        this.checkWebRTCIceTimeout();
        this.kvWebRTCService
          .startViewer(
            localView,
            remoteView,
            config,
            (stats) => {
              console.log(
                `[${dayjs(dayjs().valueOf()).format()}][LOG][WebRTC] `,
                stats
              );
            },
            (remoteMessage) => {
              console.log('[Remote Message]', remoteMessage);
            },
            (state) => {
              this.signalingClientState = state;
            },
            ({ iceState }) => {
              clearTimeout(this.webRTCIceTimeout);
              this.iceState = iceState;
              if (iceState === 'New') {
                if (!this.waitingForLiveModeStream) {
                  this.handleCameraStatus(CameraStatus.Loading);
                }
                this.errorMessage = null;
                this.signal = 1;
              } else if (iceState === 'Connected') {
                this.handleCameraStatus(CameraStatus.Connected);
                this.loading = false;
                this.errorMessage = null;
                this.initialPanZoom();
                this.checkBlankWebRTC();
                resolve();
              } else if (iceState === 'Disconnected') {
                this.errorMessage = {
                  text: '映像の再生に失敗しました。接続状態を確認して、もう一度お試しください。',
                };
                reject(new Error('Disconnected'));
              } else {
                this.iceState = 'Unknown';
                this.errorMessage = {
                  text: '映像の再生に失敗しました。接続状態を確認して、もう一度お試しください。',
                };
                reject(new Error('Unknown iceState'));
              }
            }
          )
          .catch((error) => {
            reject(error);
          });
      });
    },
    stopWebRTC() {
      if (this.kvWebRTCService) {
        this.iceState = 'Disconnected';
        this.kvWebRTCService.stopViewer();
      }
    },
    stopHLSVideo() {
      if (this.kvService) {
        this.kvService.destroy();
      }
    },
    disposeKVS(reason) {
      if (this.kvService) {
        this.stopHLSVideo();
        this.kvService.dispose(reason);
      }
    },
    async handleNeworkStatusChange(newValue) {
      if (newValue && this.kvWebRTCService && this.liveMode === 'WebRTC') {
        await this.tryPlayingWebRTC();
      }
      if (!newValue) {
        // Offline
        this.signal = 2;
      }
    },
    checkPlayVideoAfterStreamLoad() {
      const video = this.remoteViewRef || this.remoteView;

      if (this.isPlaying) {
        const promise = video.play();
        if (promise !== undefined) {
          promise.catch((e) => {
            if (
              e.name === 'NotAllowedError' ||
              e.name === 'NotSupportedError'
            ) {
              this.handleNotAllowVideoPlay();
            }
          });
        }
      } else {
        video
          .play()
          .then(() => {
            video.pause();
          })
          .catch(() => {});
        this.pauseAfterLoad = false;
      }

      clearTimeout(window[this.checkPlayVideoAfterStreamLoadTimeout]);
      window[this.checkPlayVideoAfterStreamLoadTimeout] = setTimeout(() => {
        this.cameraLoadingOnSnapshot = false;
        this.clearWaitingForNewTimestamp();
        this.fragmentIsPassingHole = false;
      }, 1000);
    },
    callVideoStreamListener(action) {
      if (action.meta && action.meta.id && action.meta.id !== this.id) {
        return;
      }
      if (action.meta && action.meta.notId && action.meta.notId === this.id) {
        return;
      }

      switch (action.type) {
        case 'kvsError':
          this.handleKVSError(action.payload, action.meta);
          break;
        case 'kvsSuccess':
          this.handleKVSuccess(action.payload);
          break;
        case 'kvsStatus':
          this.handleCameraStatus(action.payload.status);
          break;
        case 'playerStatus':
          this.handlePlayerStatusChange(action.payload, action.meta.errorCode);
          break;
        case 'changeFragmentTime':
          this.changeFragmentTime(action.payload);
          break;
        case 'seek':
          this.seek(action.payload);
          break;
        case 'changeCurrentTime':
          this.handleChangeCurrentTime(action.payload);
          break;
        case 'displayLive':
          this.displayLive();
          break;
        case 'liveMode':
          this.switchLiveMode(action.payload.mode);
          break;
        default:
          throw Error('Unknown action type "' + action.type + '"');
      }
    },
    async displayLive(notSync) {
      this.isLive = true;
      this.currentTimeIsChanging = true;
      this.startTimestamp = this.timestamp;
      this.endTimestamp = dayjs().valueOf();

      clearTimeout(window[this.displayliveTimeout]);

      window[this.displayliveTimeout] = setTimeout(() => {
        const getHLSStreamingConfig = {
          startTimestamp: this.timestamp,
          endTimestamp: this.endTimestamp,
          live: true,
          pauseAfterLoad: false,
          notSync,
          callerPath: 'displayLive',
        };
        if (notSync) {
          getHLSStreamingConfig.noLoading = true;
        }
        this.changeFragmentTime(getHLSStreamingConfig);
        this.currentTimeIsChanging = false;
      }, 1000);
    },
    handleChangeCurrentTime({ startTimestamp, endTimestamp, live }) {
      this.isLive = live;
      if (
        !live &&
        startTimestamp > this.startTimestamp &&
        startTimestamp < this.endTimestamp
      ) {
        this.seek({ startTimestamp });
      } else {
        this.timestamp = startTimestamp;
        this.changeFragmentTime({
          startTimestamp,
          endTimestamp: dayjs().valueOf(),
          live: false,
          callerPath: 'handleChangeCurrentTime',
        });
        this.startTimestamp = startTimestamp;
        this.endTimestamp = endTimestamp || dayjs().valueOf();
      }
    },
    async changeFragmentTime(args) {
      const {
        direction,
        startTimestamp,
        endTimestamp,
        live,
        pauseAfterLoad,
        noLoading,
        isEvent,
        startPosition,
        notSync,
        stopOnError,
        skipFragCheck,
      } = args;
      this.prepareForFragmentChange({
        startTimestamp,
        endTimestamp,
        pauseAfterLoad,
        live,
      });
      tracer('changeFragmentTime', {
        ...args,
        localStartTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
      });

      const video = this.remoteViewRef;
      if (live && this.liveMode === 'WebRTC') {
        // When low delay mode is enabled (WebRTC)
        await this.tryPlayingWebRTC();
      } else {
        let waitingTimestamp = startTimestamp;

        // If start position is provided, calculate the waiting timestamp based on the start position
        // because the requested timestamp is not the same as the actual video start timestamp if the start position is provided.
        // start position indecate the seconds from the start of the video as a backbuffer.
        if (startPosition) {
          waitingTimestamp = dayjs(startTimestamp)
            .add(startPosition, 'seconds')
            .valueOf();
        }

        this.setWaitingForNewTimestamp({
          newTimestamp: waitingTimestamp,
          direction,
          live,
          callerId: 'changeFragmentTime',
        });
        // When low delay mode is disabled (HLS)
        tracer('changeFragmentTime/liveMode', {
          liveMode: this.liveMode,
          isEvent,
        });
        if (isEvent) {
          this.liveMode = 'HLS';
        }
        if (
          (this.liveMode === 'WebRTC' &&
            this.currentVideoPlayingMode === 'WebRTC') ||
          isEvent
        ) {
          // When switching from WebRTC to HLS mode (when low delay mode is disabled) and the video is playing in WebRTC mode.
          // Stop WebRTC mode and switch to HLS mode.
          // This is needed because the video is playing in WebRTC mode and the user wants to switch to HLS mode on the fly.
          // The video is playing in WebRTC mode because the user is in low delay mode.
          this.waitingForLiveModeStream = 'HLS';
        }
        this.takePreviewSnapshot(noLoading, video);

        this.playHLSVideo({
          startTimestamp: this.startTimestamp,
          endTimestamp: this.endTimestamp,
          playbackMode: live ? 'LIVE' : 'ON_DEMAND',
          noLoading,
          startPosition,
          notSync,
          stopOnError,
          skipFragCheck,
        })
          .then(() => {
            console.log('Video is ready!');
          })
          .catch((error) => {
            console.error('Error when playing HLS video ', error);
          });
      }
      this.currentTimeIsChanging = false;
    },
    handleSelect(selected) {
      const index = this.cameraList.findIndex((cam) => cam.id === this.id);
      this.$emit('onSelect', { selected, id: this.id, index });
    },
    /**
     * Refresh the video stream with new `startTimestamp`
     * @param {boolean} noLoading whether show loading or capture the video to display while refreshing video stream
     */
    handleRefresh(noLoading, stopOnError) {
      this.changeFragmentTime({
        startTimestamp: this.startTimestamp,
        endTimestamp: checkHLSEndTimestamp(
          this.startTimestamp,
          this.endTimestamp
        ),
        live: this.isLive,
        pauseAfterLoad: false,
        noLoading,
        stopOnError,
        callerPath: 'handleRefresh',
      });
    },
    handleLoadMetadata(event) {
      this.updateReadyState();
      const video = event.target;
      // Calculate the ratio of the video's width to height
      this.videoMetadata.width = video.videoWidth;
      this.videoMetadata.height = video.videoHeight;
      this.videoMetadata.ratio = video.videoWidth / video.videoHeight;
      // Define the required width as 100 pixels smaller than the actual video's width
      this.videoMetadata.w = video.videoWidth - 100;
      // Calculate the height based on the video's width and the ratio
      this.videoMetadata.h = parseInt(
        this.videoMetadata.w / this.videoMetadata.ratio,
        10
      );
      // Set the canvas width and height to the values just calculated
      this.videoCanvas.width = 1280; //this.videoMetadata.w;
      this.videoCanvas.height = 720; //this.videoMetadata.h;
      // Set the canvas context to the video element
      this.videoContext = this.videoCanvas.getContext('2d');
      this.$emit('loadedmetadata', this.videoMetadat);
    },
    getNoVideoIconCanvas() {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      let p = new Path2D('M10 10 h 80 v 80 h -80 Z');
      ctx.fillRect(0, 0, 278, 156);
      ctx.fill(p);
      return canvas;
    },
    snap() {
      if (!this.videoContext) {
        return this.getNoVideoIconCanvas();
      }

      const w = 1280;
      const h = 720;
      const video = this.remoteViewRef;
      // ensure video dimensions are non-zero
      const isVideoReady =
        video.readyState === 4 && video.videoWidth > 0 && video.videoHeight > 0;

      if (!isVideoReady) {
        this.isSnapshotBlank = true;
        return this.getNoVideoIconCanvas();
      }

      // define the size of the rectangle that will be filled (basically the entire element)
      this.videoContext.fillRect(0, 0, w, h);
      // increase brightness
      this.videoContext.filter = 'brightness(115%)';
      // grab the image from the video
      this.videoContext.drawImage(video, 0, 0, w, h);

      this.isSnapshotBlank = false;
      return this.videoCanvas;
    },
    snapOption({ w, h }) {
      const video = this.remoteViewRef;
      if (!video) {
        return this.getNoVideoIconCanvas();
      }
      const canvas = document.createElement('canvas');
      // Set the canvas width and height to the values just calculated
      canvas.width = w;
      canvas.height = h;

      // Set the canvas context to the video element
      const ctx = canvas.getContext('2d');
      // Define the size of the rectangle that will be filled (basically the entire element)
      ctx.fillRect(0, 0, w, h);
      // Grab the image from the video
      ctx.drawImage(video, 0, 0, w, h);
      return canvas;
    },
    handleEsc(e) {
      if (e.keyCode === 27 && this.fullscreen) {
        this.toggleFullscreen();
      }
    },
    handleDblclick(e) {
      this.toggleFullscreen(e);
      e.preventDefault();
      e.stopPropagation();
    },
    initialPanZoom() {
      var element = this.$refs.videoWrapper;
      this.zoom = 10;
      if (element) {
        const owner = element.parentElement;
        const transformOrigin = null;
        this.panzoomInstance = panzoom(element, {
          maxZoom: this.maxZoom / 10,
          minZoom: 1,
          bounds: true,
          boundsPadding: 1,
          smoothScroll: false,
          beforeWheel: (e) => {
            if (this.disableZoom) {
              return true;
            }
            var delta = e.deltaY;
            if (e.deltaMode > 0) delta *= 100;

            var scaleMultiplier = getScaleMultiplier(delta, 0.3);

            if (scaleMultiplier !== 1) {
              var offset = transformOrigin
                ? getTransformOriginOffset(owner)
                : getOffsetXY(e, owner);
              this.zoom =
                this.panzoomInstance.getTransform().scale.toFixed(1) * 10;
              this.panzoomInstance.zoomTo(offset.x, offset.y, scaleMultiplier);
              e.preventDefault();
            }
            return true;
          },
        });
      }
    },
    // handle change zoom
    handleZoom(zoomValue) {
      if (!this.panzoomInstance) {
        this.initialPanZoom();
      }
      const centerX =
        this.$refs.videoWrapper.offsetLeft +
        this.$refs.videoWrapper.offsetWidth / 2;
      const centerY =
        this.$refs.videoWrapper.offsetTop +
        this.$refs.videoWrapper.offsetHeight / 2;
      this.panzoomInstance.zoomAbs(centerX, centerY, zoomValue / 10);
    },
    handlePrevent(e) {
      e.preventDefault();
    },
    handleDisplayMouseClick() {
      const isSelectMode =
        this.bottomController.mode === 'select' &&
        this.bottomController.selectMode !== 'none';
      if (!this.fullscreen && isSelectMode) {
        this.handleSelect(!this.bottomController.selected);
        return;
      }
      if (this.fullscreen) {
        this.displayControllerVisible = !this.displayControllerVisible;
      }
    },
    handleDisplayMouseHover() {
      this.$emit('onMouseover');
      if (!this.fullscreen) {
        this.displayControllerVisible = true;
      }
    },
    handleDisplayMouseLeave() {
      this.$emit('onMouseLeave');
      if (!this.fullscreen) {
        this.displayControllerVisible = false;
      }
    },
    // Toggling fullscreen mode
    toggleFullscreen(e) {
      if (this.kinesisVideoError) {
        return;
      }
      if (e) {
        e.stopPropagation();
      }
      if (this.fullscreen && this.zoomoutOnRestore) {
        this.handleZoom(10);
      }
      this.exitPictureInPicture();
      this.fullscreen = !this.fullscreen;
      this.displayControllerVisible = false;
      // Emit the fullscreen event
      this.$emit('onFullscreenChange', { fullscreen: this.fullscreen });
    },
    // Play video
    playHLSVideo({
      startTimestamp,
      endTimestamp,
      playbackMode,
      noLoading,
      startPosition,
      notSync,
      stopOnError,
      skipFragCheck,
    }) {
      return new Promise((resolve) => {
        if (this.checkRetentionPeriod(startTimestamp)) {
          return resolve();
        }
        const { canPlay } = this.checkCameraUpdateState(this.currentCamera);
        if (!this.id || !canPlay) {
          return resolve();
        }
        this.signal = 1;
        const camIndex = this.cameraList.findIndex((cam) => cam.id === this.id);
        this.updateCamera({ ...this.cameraList[camIndex], loading: true });
        if (!this.waitingForLiveModeStream) {
          this.loading = !noLoading;
        }
        const options = {
          accessKeyId: this.meta.accessKeyId,
          secretAccessKey: this.meta.secretAccessKey,
          sessionToken: this.meta.sessionToken,
          region: this.streamConfig.region,
          endpoint: undefined,
        };
        const playbackModeConfig = playbackMode || this.playbackMode;
        const data = {
          streamName: this.streamConfig.streamName,
          protocol: this.protocol,
          playbackMode: playbackModeConfig,
          fragmentSelectorType: this.fragmentSelectorType,
          startTimestamp: convertToISO8601(
            startTimestamp !== undefined ? startTimestamp : this.startTimestamp
          ),
          endTimestamp: convertToISO8601(
            endTimestamp !== undefined ? endTimestamp : this.endTimestamp
          ),
          containerFormat: this.containerFormat,
          discontinuityMode: this.discontinuityMode,
          displayFragmentTimestamp: this.displayFragmentTimestamp,
          maxResults:
            playbackModeConfig === 'LIVE' ? null : this.maxResultsOnDemand,
          expires: this.expires,
          id: this.id,
          startPosition,
          camera: this.currentCamera,
          device: this.$device,
          notSync,
          stopOnError,
          skipFragCheck,
        };

        this.kvService.run(options, data, EventBusEvents.CallVideoStream);

        EventBus.once(EventBusEvents.CallVideoStream, (action) => {
          if (
            action.meta &&
            action.meta.id === this.id &&
            action.type === 'playerStatus' &&
            action.payload.status === CameraStatus.Connected
          ) {
            resolve();
          }
        });
      });
    },
    emitError(code, message) {
      this.$emit('onAction', {
        type: 'error',
        payload: {
          code,
          message,
          camera: this.currentCamera,
          error: true,
          timestamp: this.timestamp,
        },
      });
    },
    // eslint-disable-next-line no-unused-vars
    handleKVSError(err, { errorCode, notSync, retry, startPosition }) {
      switch (errorCode) {
        case KVSErrorCodes.OperationCanceled:
          // intentional abort triggered by dispose/navigationm, suppress logging.
          return;

        case KVSErrorCodes.GET_DATA_ENDPOINT_TOKEN_EXP:
          console.warn('[ERROR] Kinesis Video error', err, errorCode);
          this.emitError(KVSErrorCodes.GET_DATA_ENDPOINT_TOKEN_EXP, err);
          return;

        case KVSErrorCodes.NO_FRAGMENT_FOUND:
          console.warn('[ERROR] Kinesis Video error', err, errorCode);
          this.errorMessage = this.isLive
            ? VideoStreamErrorMessages.LiveNoFragmentFound
            : { text: '録画映像なし', theme: 'dark-bg' };
          if (notSync) {
            this.stopHLSVideo();
          } else {
            if (this.checkVideoPlaying()) {
              this.stopHLSVideo();
            }
            // In the case user choose a timestamp in VOD mode the seek-bar will resume but the player do not make any action.
            // Only on LIVE or the FIRST retry it should reset player.
            if (this.isLive || retry) {
              this.endLoading(false);

              this.signal = 2;
              this.isNoFragmentFound = true;
              this.handleCameraStatus(KVSErrorCodes.NO_FRAGMENT_FOUND);
              const newTimestamp = startPosition
                ? dayjs(this.startTimestamp)
                    .add(startPosition, 'seconds')
                    .valueOf()
                : this.startTimestamp;
              this.resetPlayerTimerWithNewTimestamp(newTimestamp);
            }
          }
          this.clearWaitingForNewTimestamp();
          return;

        default:
          console.warn('[ERROR] Kinesis Video error', err, errorCode);
          this.errorMessage = this.isLive
            ? {
                text: '映像の再生に失敗しました。接続状態を確認して、もう一度お試しください。',
              }
            : { text: '録画映像なし', theme: 'dark-bg' };

          this.endLoading(true);
          this.stopHLSVideo();
          this.signal = 2;
          this.handleCameraStatus(CameraStatus.Error);
          this.kinesisVideoError = true;
          this.clearWaitingForNewTimestamp();
      }
    },
    async handleKVSuccess({ HLSStreamingSessionURL, startPosition, signal }) {
      this.errorMessage = null;
      this.kinesisVideoError = false;
      this.startCheckLiveInterval();

      clearTimeout(window[this.zoomTimeout]);
      window[this.zoomTimeout] = setTimeout(this.initialPanZoom, 1000);

      const remoteView = this.remoteViewRef || this.remoteView;
      if (!remoteView) {
        throw new Error('remoteView is not defined');
      }

      try {
        this.kvService.playHls(
          HLSStreamingSessionURL,
          remoteView,
          EventBusEvents.CallVideoStream,
          this.id,
          startPosition,
          signal
        );
        if (startPosition) {
          remoteView.currentTime = getFiniteNumber(startPosition);
        }
        this.handlePlayerDone();
      } catch (error) {
        this.handleNoData();
        console.error(error);
      }
    },
    handleCameraStatus(status) {
      this.cameraStatus = status;
    },
    handlePlayerStatusChange(data = {}) {
      const { status, frag, prevFrag, playlist, startPosition, holes } = data;

      const statusHandlers = {
        [CameraStatus.Error]: () => handlers.handleError(this),
        [CameraStatus.Connected]: () => handlers.handleConnected(this),
        [CameraStatus.Loaded]: () => handlers.handleLoaded(this),
        [CameraStatus.Loading]: () => handlers.handleLoading(this),
        [PlayerStatus.PlaylistChanged]: () =>
          handlers.handlePlaylistChanged(this, playlist, holes),
        [PlayerStatus.FragmentStatusChanged]: () =>
          handlers.handleFragmentStatusChanged(this, frag),
        [PlayerStatus.FragmentBuffered]: () =>
          handlers.handleFragmentBuffered(this, frag),
        [PlayerStatus.CodecIsNotTheSame]: () =>
          handlers.handleKVSCodecIsNotTheSame(this, data),
        [PlayerStatus.StreamingSessionExpired]: () =>
          handlers.handleStreamingSessionExpired(this),
        [PlayerStatus.NoFragmentChanged]: () =>
          handlers.handleNoFragmentChange(this),
        [PlayerStatus.CanNotRecover]: () =>
          handlers.handleCanNotRecover(this, frag),
        [PlayerStatus.FragmentLoaded]: () =>
          handlers.handleFragmentLoaded(this, frag, prevFrag),
        [PlayerStatus.FragmentChanged]: () =>
          handlers.handleFragmentChanged(this, {
            frag,
            prevFrag,
            startPosition,
          }),
      };

      const handler =
        statusHandlers[status] || (() => handlers.handleNoData(this));
      handler();
    },
    handlePlayerDone() {
      const remoteView = this.remoteViewRef || this.remoteView;
      this.endLoading();

      this.$nextTick(() => {
        remoteView.playbackRate = this.playbackRate;
      });
    },
    async timeupdate() {
      if (
        this.currentTimeIsChanging ||
        !this.firstFragment ||
        this.fragmentIsPassingHole
      ) {
        return;
      }

      const video = this.remoteViewRef;
      // handle fragment hole events
      const eventHanlder = (action) => {
        if (action.type === FragmentHoleType.SkipOverlap) {
          this.fragmentIsPassingHole = action.isSkipping;
        }
        if (action.type === FragmentHoleType.VideoOff) {
          handleVideoOff(this, action.gap);
        }
        if (action.type === FragmentHoleType.CodecChanged) {
          this.fragmentIsPassingHole = true;
          tracer('[video-stream]/timeupdate/FragmentHoleType.CodecChanged', {
            gap: action.gap,
            endFrag: action.gap.endFrag,
            programDateTime: action.gap.endFrag.programDateTime,
          });
          this.handleRenewOnCodecChange(action.gap.endFrag.programDateTime);
        }
      };
      if (this.isPlaying && !this.isDiscontinuity && !this.errorMessage) {
        if (
          await handleVideoHole(video, this.allFragmentHoles, eventHanlder, {
            device: this.$device,
          })
        ) {
          return;
        }
      }
    },
    updateCurrentTimestamp(val) {
      let updateTime = val;
      // check if video is waiting for new timestamp requested by end-user
      if (this.waitingBackwardTimestamp || this.waitingForwardTimestamp) {
        const waitingTimestamp =
          this.waitingBackwardTimestamp || this.waitingForwardTimestamp;
        updateTime = dayjs(waitingTimestamp).valueOf();
      }
      this.$emit('timeupdate', updateTime);
      this.timestamp = updateTime;
      if (
        this.isDiscontinuity &&
        checkVideoOffEnded({
          timestamp: this.timestamp,
          hole: this.currentVideoHole,
          onVideoEnd: (payload) => {
            endDiscontinuity(this, payload);
          },
        })
      ) {
        return;
      }
    },
    playTracking() {
      const video = this.remoteViewRef;

      if (!video || this.isDestroyed) {
        return;
      }
      if (!this.fragmentIsPassingHole) {
        this.timeupdate({ target: video });
      }
      this.reqId = requestAnimationFrame(this.playTracking.bind(this));
    },
    startTracking(e) {
      this.timeupdate(e);
      this.reqId = requestAnimationFrame(this.playTracking.bind(this));
    },
    stopTracking() {
      cancelAnimationFrame(this.reqId);

      if (this.isDiscontinuity) {
        return;
      }
      this.pausePlayerTimer();
    },
    handlePlaying() {
      clearTimeout(window[this.unwantedPauseTimeout]);
      window[this.unwantedPauseTimeout] = undefined;

      this.isBuffering = false;
      if (this.isPlaying && !this.isDiscontinuity) {
        this.startPlayerTimer();
        this.lastCodecSequenceChange = null;
      }
    },
    handleWaiting() {
      this.isBuffering = true;
      if (!this.isNoFragmentFound && !this.isDiscontinuity) {
        this.playerTimer.stop();
      }
    },
    pausePlayerOnVideoOff(pauseTime, hole) {
      const video = this.remoteViewRef || this.remoteView;
      if (!video) {
        return;
      }
      this.pausePlayer({
        afterPause: () => {
          if (pauseTime !== null && pauseTime !== undefined) {
            video.currentTime = getFiniteNumber(pauseTime);
          }
          handleVideoOff(this, hole);
        },
      });
    },
    /**
     * Seek a new timestamp
     * @param {*} param0
     * @returns
     */
    async seek({
      startTimestamp,
      pauseAfterLoad,
      value,
      controller,
      startPosition,
      endTimestamp: endTimestampRquest,
      live,
    } = {}) {
      const video = this.remoteViewRef || this.remoteView;

      // Seek LIVE stream
      if (live) {
        this.displayLive();
        return;
      }

      // Seek VOD stream
      const signal = this.getFragmentCheckSignal();
      this.currentTimeIsChanging = true;
      this.setWaitingForNewTimestamp({
        newTimestamp: startTimestamp,
        callerId: 'seek:' + controller,
      });

      if (this.waitingTimestampCallerId === 'changeFragmentTime') {
        this.kvService.stopLoad();
        const disposeReason =
          'While seeking forward, new request for fragment was in progress';
        this.kvService.dispose(disposeReason);
      }
      this.seekVOD({
        controller,
        startTimestamp,
        endTimestampRquest,
        startPosition,
        pauseAfterLoad,
        signal,
        video,
        value,
      });
    },
    async seekVOD({
      controller,
      startTimestamp,
      endTimestampRquest,
      startPosition,
      pauseAfterLoad,
      signal,
      video,
      value,
    }) {
      if (controller === VideoControllersType.DragSeekbar) {
        this.kvService.stopLoad();
        // Seek a timestamp (*)
        await seekTimestamp(
          this,
          startTimestamp,
          endTimestampRquest,
          startPosition,
          pauseAfterLoad,
          signal
        );
      } else if (controller === VideoControllersType.BackwardSeekbar) {
        // Seek backward (<-)
        await seekBackwardTimestamp(this, video, startTimestamp, signal);
        return;
      } else {
        // seek forward (->)
        seekForwardTimestamp(this, video, startTimestamp, value, signal);
      }
    },
    /**
     * End loading camera
     * @param {*} error Ended loading camera with error
     */
    endLoading(error = false) {
      this.loading = false;
      const camIndex = this.cameraList.findIndex((cam) => cam.id === this.id);
      this.updateCamera({ ...this.cameraList[camIndex], loading: true });
      let doneLoading = true;
      this.cameraList.forEach((el) => {
        if (el.loading && el.id !== this.id) {
          doneLoading = false;
        }
      });
      if (doneLoading) {
        this.isPlaying = !this.pauseAfterLoad;

        const video = this.remoteViewRef || this.remoteView;
        if (video) {
          if (error || this.pauseAfterLoad) {
            video
              .play()
              .then(() => {
                video.pause();
              })
              .catch(() => {});
          } else if (!this.checkVideoPlaying()) {
            this.playPlayer({ target: video });
          }
        } else {
          console.error(
            '[ERROR] Error on HLS loading end. Video element is undefined'
          );
        }
      }
    },
    ...mapActions('common', ['updateCamera']),
    ...mapActions('settings', ['autofocus']),
    ...mapActions('video', ['setLowLatencyModeById']),
    ...mapActions('help', ['setSelectedCam']),
    handleChangeLiveMode(status) {
      const liveMode = status ? 'WebRTC' : 'HLS';
      this.$emit('onLiveMode', liveMode);
      this.setLowLatencyModeById({
        id: this.currentCamera.id,
        latencyMode: liveMode,
      });
    },
    handleHelp() {
      this.setSelectedCam(this.currentCamera);
      this.routerPush({
        name: 'help-cat-list',
        params: { id: '2', topic: '2' },
        query: { cam: this.currentCamera.id },
      });
    },
  },
};
